import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { fmtEur } from '../../lib/utils';
import { CYCLES, CYCLE_LABEL, METHODS, METHOD_LABEL, isoDay, type Cycle, type PayMethod } from '../../features/admin/adminApi';

/**
 * Spesa ricorrente (canone, affitto, abbonamento): nome, a chi, importo netto,
 * IVA, ogni quanto, prossima scadenza, metodo. Salva con Ctrl/Cmd+Invio.
 */
export function NewRecurringModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: '',
    counterparty: '',
    amountNet: '',
    vatRate: '22',
    cycle: 'MONTHLY' as Cycle,
    nextDueDate: isoDay(),
    method: 'BONIFICO' as PayMethod,
  });
  const net = Number(String(form.amountNet).replace(',', '.'));
  const vat = Number(form.vatRate) || 0;
  const gross = Number.isFinite(net) ? net * (1 + vat / 100) : 0;

  const create = useMutation({
    mutationFn: () => api.post<{ id: string }>('/admin/recurring', {
      name: form.name.trim(),
      counterparty: form.counterparty.trim() || null,
      amountNet: net,
      vatRate: vat,
      cycle: form.cycle,
      dayOfMonth: Number(form.nextDueDate.slice(8, 10)) || null,
      startDate: form.nextDueDate,
      nextDueDate: form.nextDueDate,
      active: true,
      method: form.method,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      toast.ok('Spesa ricorrente creata', `${form.name} · € ${fmtEur(gross, true)} ${CYCLE_LABEL[form.cycle].toLowerCase()}`);
      onClose();
    },
    onError: (err) => toast.alert('Non salvata', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const canSubmit = !!form.name.trim() && Number.isFinite(net) && net > 0 && !!form.nextDueDate && !create.isPending;
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canSubmit) { e.preventDefault(); create.mutate(); }
  };

  return (
    <Modal
      title="Spesa ricorrente"
      kicker="Amministrazione"
      onClose={onClose}
      size="md"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => create.mutate()} disabled={!canSubmit}>
            Crea <Icon name="arrowRight" size={12} />
          </button>
        </>
      }
    >
      <div className="form-grid" onKeyDown={onKeyDown}>
        <Field label="Nome" required span={12}>
          <input className="inp" autoFocus placeholder="Es. Affitto studio, Google Workspace" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="A chi" span={12} hint="Facoltativo: serve per riconoscere il movimento in banca">
          <input className="inp" placeholder="Fornitore" value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} />
        </Field>
        <Field label="Importo netto" required span={4}>
          <input className="inp mono" inputMode="decimal" placeholder="0,00" value={form.amountNet} onChange={(e) => setForm({ ...form, amountNet: e.target.value })} />
        </Field>
        <Field label="IVA %" span={3}>
          <select className="inp" value={form.vatRate} onChange={(e) => setForm({ ...form, vatRate: e.target.value })}>
            <option value="22">22</option>
            <option value="10">10</option>
            <option value="4">4</option>
            <option value="0">0</option>
          </select>
        </Field>
        <Field label="Lordo" span={5}>
          <div className="inp mono" style={{ display: 'flex', alignItems: 'center', background: 'var(--paper-2)' }}>€ {fmtEur(gross, true)}</div>
        </Field>
        <Field label="Ogni quanto" span={6}>
          <select className="inp" value={form.cycle} onChange={(e) => setForm({ ...form, cycle: e.target.value as Cycle })}>
            {CYCLES.map((c) => <option key={c} value={c}>{CYCLE_LABEL[c]}</option>)}
          </select>
        </Field>
        <Field label="Prossima scadenza" required span={6}>
          <input className="inp mono" type="date" value={form.nextDueDate} onChange={(e) => setForm({ ...form, nextDueDate: e.target.value })} />
        </Field>
        <Field label="Metodo" span={12}>
          <select className="inp" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value as PayMethod })}>
            {METHODS.map((m) => <option key={m} value={m}>{METHOD_LABEL[m]}</option>)}
          </select>
        </Field>
      </div>
    </Modal>
  );
}
